import $ from "../lib/jquery.js";
import { executeRecaptcha, hasRecaptcha } from "./recaptcha.js";

/*
 * Client-side validation for .reg-form.
 *
 * Behavioural classes, set in the markup:
 *   .required   must not be empty (also works on checkboxes)
 *   .email      must look like an email address
 *   .postcode   must be at least 3 characters
 *
 * This is a convenience for the visitor only. content/inc.form-submission.php
 * re-checks everything server side before the enquiry is saved or forwarded.
 */

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/** Track which forms the visitor has already tried to submit. */
const attempted = new WeakSet();

function setFieldValidity($field, valid) {
  $field.toggleClass("error", !valid);
  $field.attr("aria-invalid", valid ? null : "true");

  // Custom selects render a sibling element that needs the class too.
  if ($field.hasClass("customSelect")) {
    $field.next(".customSelect").toggleClass("error", !valid);
  }
}

function validateField(field) {
  const $field = $(field);
  const value = ($field.val() ?? "").toString().trim();

  let valid = true;

  if ($field.hasClass("required") && value === "") {
    valid = false;
  }

  if (valid && $field.hasClass("email") && !EMAIL_PATTERN.test(value)) {
    valid = false;
  }

  if (valid && $field.hasClass("postcode") && value !== "" && value.length < 3) {
    valid = false;
  }

  setFieldValidity($field, valid);

  return valid;
}

function validateCheckbox(checkbox) {
  const $checkbox = $(checkbox);

  if (!$checkbox.hasClass("required")) {
    return true;
  }

  const valid = $checkbox.is(":checked");

  $checkbox.attr("aria-invalid", valid ? null : "true");
  $checkbox.next("label").toggleClass("error", !valid);

  return valid;
}

export function validateForm(form) {
  const $form = $(form);
  let valid = true;

  $form.find("input, textarea, select").each((_, field) => {
    const $field = $(field);
    const isCheckable = $field.is(':checkbox, :radio, [type="hidden"], [type="submit"]');

    if (isCheckable) {
      return;
    }

    valid = validateField(field) && valid;
  });

  $form.find('input[type="checkbox"]').each((_, checkbox) => {
    valid = validateCheckbox(checkbox) && valid;
  });

  return valid;
}

/** Submit for real, bypassing the preventDefault handler below. */
export function submitForm(form) {
  const $form = $(form);

  $form.find(".submit-button").prop("disabled", true);
  $form.off("submit");
  form.submit();
}

function handleSubmitAttempt(form) {
  attempted.add(form);

  if (!validateForm(form)) {
    $(form).find(".error, [aria-invalid='true']").first().trigger("focus");
    return;
  }

  const $form = $(form);

  if (!hasRecaptcha()) {
    submitForm(form);
    return;
  }

  $form.find(".submit-button").prop("disabled", true);

  executeRecaptcha(
    (token) => {
      $form.find('input[name="token"]').val(token);
      submitForm(form);
    },
    () => $form.find(".submit-button").prop("disabled", false),
  );
}

export default function initForms() {
  const $forms = $(".reg-form");

  if (!$forms.length) {
    return;
  }

  // Submission is driven by the button handler so validation can gate it.
  $forms.on("submit", (event) => event.preventDefault());

  // Re-validate a field on blur, but only once the visitor has tried to submit
  // — flagging errors before then is hostile.
  $forms.on("blur", "input, textarea, select", (event) => {
    if (attempted.has(event.delegateTarget)) {
      validateField(event.currentTarget);
    }
  });

  $forms.on("change", 'input[type="checkbox"]', (event) => {
    if (attempted.has(event.delegateTarget)) {
      validateCheckbox(event.currentTarget);
    }
  });

  $forms.on("click", ".submit-button", (event) => {
    event.preventDefault();
    handleSubmitAttempt(event.delegateTarget);
  });
}
